import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { NetsuiteImportSearch } from '../../models/appcloud/netsuite/netsuite-import-search.model';
import { NetsuiteImport } from '../../models/appcloud/netsuite/netsuite-import.model';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { HttpHeaderConstants } from '../http-header.constant';
import { SpinnerService } from '../spinner';

@Injectable({
    providedIn: 'root',
})
export class NetsuiteImportService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private NETSUITE_IMPORTS_URL: string = environment.publicApiHost + '/appcloud/netsuite-imports';

    public findAll(
        netsuiteImportSearch: NetsuiteImportSearch,
        additionalHeaders: any = {}
    ): Observable<NetsuiteImport[]> {
        this.#spinnerService.addProcess(
            'NetsuiteImportService.findAll.' + netsuiteImportSearch.eloquainstallId,
            'Loading your imports...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams({ fromObject: <any>netsuiteImportSearch }),
        };

        return this.#http.get<NetsuiteImport[]>(this.NETSUITE_IMPORTS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving NetSuite Import data from Relationship One. Please try again later.'
                );
                return of<NetsuiteImport[]>();
            }),
            finalize(() =>
                this.#spinnerService.completeProcess(
                    'NetsuiteImportService.findAll.' + netsuiteImportSearch.eloquainstallId
                )
            )
        );
    }

    public create(netsuiteImport: NetsuiteImport, additionalHeaders: any = {}): Observable<NetsuiteImport> {
        this.#spinnerService.addProcess('NetsuiteImportService.create', 'Creating your NetSuite import...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };
        return this.#http.post<NetsuiteImport>(this.NETSUITE_IMPORTS_URL, netsuiteImport, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while creating your NetSuite import. Please try again later.'
                );
                return of<NetsuiteImport>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteImportService.create'))
        );
    }

    public update(id: number, netsuiteImport: NetsuiteImport, additionalHeaders: any = {}): Observable<NetsuiteImport> {
        this.#spinnerService.addProcess('NetsuiteImportService.update', 'Updating your NetSuite import...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.mergePatch, ...additionalHeaders }),
        };
        return this.#http.patch<NetsuiteImport>(this.NETSUITE_IMPORTS_URL + '/' + id, netsuiteImport, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while updating your NetSuite import. Please try again later.'
                );
                return of<NetsuiteImport>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteImportService.update'))
        );
    }

    public delete(id: number, netsuiteImport: NetsuiteImport, additionalHeaders: any = {}): Observable<void> {
        this.#spinnerService.addProcess('NetsuiteImportService.delete', 'Deleting your NetSuite import...');
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.mergePatch, ...additionalHeaders }),
            body: netsuiteImport,
        };
        return this.#http.delete<void>(this.NETSUITE_IMPORTS_URL + '/' + id, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while deleting your NetSuite import. Please try again later.'
                );
                return of<void>();
            }),
            finalize(() => this.#spinnerService.completeProcess('NetsuiteImportService.delete'))
        );
    }
}
